/**
 * Comprehensive Expense Parsing Test Cases
 * 30 real-world scenarios across languages, formats, and edge cases
 */

export interface ExpenseTestCase {
  id: string;
  category: string;
  input: string;
  expected: {
    amount: number | null;
    currency: string;
    merchant: string | null;
    category: string | null;
    date?: string; // ISO format or 'today' or 'yesterday'
    note?: string;
  };
  notes: string;
  allowLLMFallback?: boolean; // If true, LLM fallback is expected/acceptable
}

export const expenseParsingTests: ExpenseTestCase[] = [
  // ===== Category 1: Simple English Inputs =====
  {
    id: "test-01",
    category: "Simple English",
    input: "$45 Starbucks",
    expected: {
      amount: 45.00,
      currency: "USD",
      merchant: "Starbucks",
      category: "Food",
    },
    notes: "Most common format for US users"
  },
  {
    id: "test-02",
    category: "Simple English",
    input: "Walmart 23.50",
    expected: {
      amount: 23.50,
      currency: "EUR", // default currency
      merchant: "Walmart",
      category: "Food",
    },
    notes: "Tests default currency assumption"
  },
  {
    id: "test-03",
    category: "Simple English",
    input: "coffee yesterday 5.50€",
    expected: {
      amount: 5.50,
      currency: "EUR",
      merchant: "Coffee Shop",
      category: "Food",
      date: "yesterday",
    },
    notes: "Tests relative date parsing + keyword detection"
  },
  {
    id: "test-04",
    category: "Simple English",
    input: "Gas 15/11/2025 $60",
    expected: {
      amount: 60.00,
      currency: "USD",
      merchant: "Gas Station",
      category: "Transport",
      date: "2025-11-15",
    },
    notes: "Tests European date format (DD/MM/YYYY)"
  },
  {
    id: "test-05",
    category: "Simple English",
    input: "Lunch at McDonald's $12.99 yesterday",
    expected: {
      amount: 12.99,
      currency: "USD",
      merchant: "McDonald's",
      category: "Food",
      date: "yesterday",
    },
    notes: "Tests natural language with brand detection"
  },
  {
    id: "test-06",
    category: "Simple English",
    input: "€15,50 LIDL",
    expected: {
      amount: 15.50,
      currency: "EUR",
      merchant: "LIDL",
      category: "Food",
    },
    notes: "Tests comma as decimal separator"
  },

  // ===== Category 2: Multi-Language Support =====
  {
    id: "test-07",
    category: "Multi-Language",
    input: "продукти 350₴ АТБ",
    expected: {
      amount: 350.00,
      currency: "UAH",
      merchant: "АТБ",
      category: "Food",
    },
    notes: "Tests Ukrainian keyword + brand + currency symbol"
  },
  {
    id: "test-08",
    category: "Multi-Language",
    input: "кава вчора 45 грн",
    expected: {
      amount: 45.00,
      currency: "UAH",
      merchant: "Coffee Shop",
      category: "Food",
      date: "yesterday",
    },
    notes: "Tests Ukrainian keyword + 'вчора' (yesterday) + 'грн' text currency"
  },
  {
    id: "test-09",
    category: "Multi-Language",
    input: "cena en Mercadona 23€ ayer",
    expected: {
      amount: 23.00,
      currency: "EUR",
      merchant: "Mercadona",
      category: "Food",
      date: "yesterday",
    },
    notes: "Tests Spanish text with European brand",
    allowLLMFallback: true
  },
  {
    id: "test-10",
    category: "Multi-Language",
    input: "Uber 18,50€ gestern",
    expected: {
      amount: 18.50,
      currency: "EUR",
      merchant: "Uber",
      category: "Transport",
      date: "yesterday",
    },
    notes: "Tests German date word (may need LLM fallback)",
    allowLLMFallback: true
  },
  {
    id: "test-11",
    category: "Multi-Language",
    input: "Zara 89,99€ hier",
    expected: {
      amount: 89.99,
      currency: "EUR",
      merchant: "Zara",
      category: "Shopping",
      date: "yesterday",
    },
    notes: "Tests French date word (may need LLM fallback)",
    allowLLMFallback: true
  },
  {
    id: "test-12",
    category: "Multi-Language",
    input: "Albert Heijn €34,20 gisteren",
    expected: {
      amount: 34.20,
      currency: "EUR",
      merchant: "Albert Heijn",
      category: "Food",
      date: "yesterday",
    },
    notes: "Tests Dutch brand + date word",
    allowLLMFallback: true
  },
  {
    id: "test-13",
    category: "Multi-Language",
    input: "farmácia ontem R$ 45,50",
    expected: {
      amount: 45.50,
      currency: "BRL",
      merchant: "Pharmacy",
      category: "Health",
      date: "yesterday",
    },
    notes: "Tests Portuguese + Brazilian Real currency",
    allowLLMFallback: true
  },
  {
    id: "test-14",
    category: "Multi-Language",
    input: "cena ieri 35€ Carrefour",
    expected: {
      amount: 35.00,
      currency: "EUR",
      merchant: "Carrefour",
      category: "Food",
      date: "yesterday",
    },
    notes: "Tests Italian date word",
    allowLLMFallback: true
  },

  // ===== Category 3: Edge Cases & Ambiguity =====
  {
    id: "test-15",
    category: "Edge Cases",
    input: "Starbucks yesterday",
    expected: {
      amount: null,
      currency: "EUR",
      merchant: "Starbucks",
      category: "Food",
      date: "yesterday",
    },
    notes: "Should ask user 'How much did you spend?'",
    allowLLMFallback: true
  },
  {
    id: "test-16",
    category: "Edge Cases",
    input: "€25 yesterday",
    expected: {
      amount: 25.00,
      currency: "EUR",
      merchant: null,
      category: null,
      date: "yesterday",
    },
    notes: "Should ask 'What did you buy?' or 'Where?'",
    allowLLMFallback: true
  },
  {
    id: "test-17",
    category: "Edge Cases",
    input: "Amazon $150",
    expected: {
      amount: 150.00,
      currency: "USD",
      merchant: "Amazon",
      category: "Shopping", // Most likely category
    },
    notes: "Amazon could be multiple categories - should pick most common"
  },
  {
    id: "test-18",
    category: "Edge Cases",
    input: "Had a great dinner with friends at this amazing Italian restaurant downtown, spent about €75 on pasta and wine last Friday",
    expected: {
      amount: 75.00,
      currency: "EUR",
      merchant: "Restaurant",
      category: "Food",
      note: "Had a great dinner with friends",
    },
    notes: "Tests natural language + complex date + note extraction",
    allowLLMFallback: true
  },
  {
    id: "test-19",
    category: "Edge Cases",
    input: "Coffee $5 and lunch $15 at Whole Foods",
    expected: {
      amount: 20.00, // Sum or could be split into 2 expenses
      currency: "USD",
      merchant: "Whole Foods",
      category: "Food",
    },
    notes: "Tests multi-item parsing",
    allowLLMFallback: true
  },
  {
    id: "test-20",
    category: "Edge Cases",
    input: "Trader Joe's $45.99",
    expected: {
      amount: 45.99,
      currency: "USD",
      merchant: "Trader Joe's",
      category: "Food",
    },
    notes: "Tests apostrophe handling in brand names"
  },
  {
    id: "test-21",
    category: "Edge Cases",
    input: "$150 in London hotel",
    expected: {
      amount: 150.00,
      currency: "USD",
      merchant: "Hotel",
      category: "Housing",
      note: "in London",
    },
    notes: "Tests location context vs currency symbol",
    allowLLMFallback: true
  },
  {
    id: "test-22",
    category: "Edge Cases",
    input: "Wallmart 35$",
    expected: {
      amount: 35.00,
      currency: "USD",
      merchant: "Walmart", // Fuzzy match or LLM correction
      category: "Food",
    },
    notes: "Tests typo tolerance (may need LLM fallback)",
    allowLLMFallback: true
  },

  // ===== Category 4: Complex Real-World Scenarios =====
  {
    id: "test-23",
    category: "Complex Scenarios",
    input: "CVS 11/14/2025 - tissues $3.99, medicine $12.50, total $16.49",
    expected: {
      amount: 16.49,
      currency: "USD",
      merchant: "CVS",
      category: "Health",
      date: "2025-11-14",
    },
    notes: "Tests parsing total from multi-item receipt",
    allowLLMFallback: true
  },
  {
    id: "test-24",
    category: "Complex Scenarios",
    input: "3 days ago Uber ride £28.50",
    expected: {
      amount: 28.50,
      currency: "GBP",
      merchant: "Uber",
      category: "Transport",
    },
    notes: "Tests 'N days ago' parsing",
    allowLLMFallback: true
  },
  {
    id: "test-25",
    category: "Complex Scenarios",
    input: "Dinner November 10 at KFC $25",
    expected: {
      amount: 25.00,
      currency: "USD",
      merchant: "KFC",
      category: "Food",
      date: "2025-11-10",
    },
    notes: "Tests month name parsing",
    allowLLMFallback: true
  },
  {
    id: "test-26",
    category: "Complex Scenarios",
    input: "Groceries at Сільпо €45 вчора",
    expected: {
      amount: 45.00,
      currency: "EUR",
      merchant: "Сільпо",
      category: "Food",
      date: "yesterday",
    },
    notes: "Tests Ukrainian brand + English keyword + Ukrainian date"
  },
  {
    id: "test-27",
    category: "Complex Scenarios",
    input: "Netflix subscription $15.99 monthly",
    expected: {
      amount: 15.99,
      currency: "USD",
      merchant: "Netflix",
      category: "Bills/Other",
      note: "monthly",
    },
    notes: "Tests subscription context",
    allowLLMFallback: true
  },
  {
    id: "test-28",
    category: "Complex Scenarios",
    input: "ATM withdrawal $100",
    expected: {
      amount: 100.00,
      currency: "USD",
      merchant: "ATM",
      category: "Bills/Other",
    },
    notes: "Tests non-purchase expense type"
  },
  {
    id: "test-29",
    category: "Complex Scenarios",
    input: "Taxi 50₴",
    expected: {
      amount: 50.00,
      currency: "UAH",
      merchant: "Taxi",
      category: "Transport",
    },
    notes: "Tests integer amounts (no decimals)"
  },
  {
    id: "test-30",
    category: "Complex Scenarios",
    input: "spent like twenty bucks on chipotle today",
    expected: {
      amount: 20.00,
      currency: "USD",
      merchant: "Chipotle",
      category: "Food",
    },
    notes: "Tests natural speech patterns (requires LLM)",
    allowLLMFallback: true
  },
];
